import { useState } from "react";
import { X, Copy, Check } from "lucide-react";
import { PopupModal } from "../../../components/Modal";
import CustomButton from "../../../components/Button";
import { ShowToast } from "../../../components/toast";

const EXAMPLE_TEMPLATE = `# [Document Title]

## [Topic / Section Name]

### [A question a user might ask]

**Short answer:** [One sentence that directly answers the question.]

**Details:** [Longer explanation — context, steps, caveats, examples.]

**Related:** [Pointers to related questions or sections in this doc.]

### [Another question in the same topic]

**Short answer:** [...]

**Details:** [...]

**Related:** [...]

## [Another Topic / Section Name]

### [Question]

**Short answer:** [...]

**Details:** [...]

**Related:** [...]
`;

type ExampleStructureModalProps = {
  onClose: () => void;
};

export function ExampleStructureModal({ onClose }: ExampleStructureModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(EXAMPLE_TEMPLATE);
      setCopied(true);
      ShowToast({ message: "Template copied to clipboard", type: "success" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      ShowToast({ message: "Could not copy template", type: "error" });
    }
  };

  return (
    <PopupModal
      onClose={onClose}
      contentClassName="max-w-2xl w-full mx-auto max-h-[90vh] overflow-hidden"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-content-primary text-xl font-semibold">
            Example document structure
          </h3>
          <p className="text-content-secondary text-sm">
            Fill in anything inside <code className="px-1 py-0.5 rounded bg-surface-secondary text-xs">[brackets]</code> with your own content.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="p-1 rounded text-content-secondary hover:text-content-primary hover:bg-surface-hover transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="rounded-lg border border-border-secondary bg-surface-secondary/60 p-4 text-xs text-content-secondary leading-relaxed">
        <p className="mb-2">Each document is a single Markdown file made of:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>One H1</strong> (<code>#</code>) — the document title.</li>
          <li><strong>H2 sections</strong> (<code>##</code>) — group related topics.</li>
          <li><strong>H3 questions</strong> (<code>###</code>) — phrase as something a user would ask.</li>
          <li>Under each question, include <strong>Short answer</strong>, <strong>Details</strong>, and <strong>Related</strong>.</li>
        </ul>
      </div>

      <div className="relative">
        <pre className="max-h-[50vh] overflow-auto rounded-lg border border-border-secondary bg-neutral-900 text-neutral-100 p-4 text-xs leading-relaxed font-mono whitespace-pre">
          {EXAMPLE_TEMPLATE}
        </pre>
      </div>

      <div className="flex items-center justify-end gap-2">
        <CustomButton variant="outline" onClick={onClose}>
          Close
        </CustomButton>
        <CustomButton onClick={handleCopy}>
          {copied ? (
            <>
              <Check className="w-4 h-4" />
              Copied
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copy template
            </>
          )}
        </CustomButton>
      </div>
    </PopupModal>
  );
}
